import { Component, OnInit, inject } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import IncidentService from 'src/app/shared/services/incident.service';
import { Incident, IncidentEvent, IncidentStatus } from '../../incident-models';
import { firstValueFrom } from 'rxjs';
import { TranslateIncidentStatusEnumPipe } from '../../../../shared/pipes/translate-incident-status-enum.pipe';
import { FormatIncidentPipe } from '../../../../shared/pipes/format-incident.pipe';
import { CustomDropDownArrowDirective } from 'ok-angular-shared/directives';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'adm-incident-status-modal',
  templateUrl: './incident-status-modal.component.html',
  styleUrls: ['./incident-status-modal.component.scss'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatOptionModule,
    MatButtonModule,
    MatIconModule,
    CustomDropDownArrowDirective,
    TranslateIncidentStatusEnumPipe,
    FormatIncidentPipe,
  ],
})
export class IncidentStatusModalComponent implements OnInit {
  dialogRef = inject<MatDialogRef<IncidentStatusModalComponent>>(MatDialogRef);
  private incidentService = inject(IncidentService);
  data = inject<{
    incident: Incident;
  }>(MAT_DIALOG_DATA);

  public currentStatus: IncidentStatus = IncidentStatus.Reported;
  public incidentStatus: typeof IncidentStatus = IncidentStatus;
  public incidentEvent: IncidentEvent;
  public showForm = true;

  public form: FormGroup = new FormGroup({
    incidentId: new FormControl<number | null>(null, [Validators.required]),
    status: new FormControl<IncidentStatus>(IncidentStatus.Reported, { nonNullable: true }),
    comment: new FormControl<string | null>(null, [Validators.required, this.noWhitespaceValidator]),
  });

  get incidentId() {
    return this.form.get('incidentId') as FormControl<number | null>;
  }
  get status() {
    return this.form.get('status') as FormControl<IncidentStatus>;
  }
  get comment() {
    return this.form.get('comment') as FormControl<string | null>;
  }

  ngOnInit(): void {
    this.incidentId?.setValue(this.data.incident.id);
    this.status?.setValue(this.data.incident.status);
    this.currentStatus = this.data.incident.status;

    if (this.currentStatus === IncidentStatus.Closed) {
      this.status?.setValue(IncidentStatus.Active);
    }
  }

  public noWhitespaceValidator(control: AbstractControl): ValidationErrors | null {
    const isWhitespace = (control.value || '').trim().length === 0 && control.value != null;
    const isValid = !isWhitespace;
    return isValid ? null : { whitespace: true };
  }

  submitForm() {
    if (this.form.valid) {
      const incidentId = this.incidentId.value;
      if (incidentId == null) {
        return;
      }

      if (this.status.value === IncidentStatus.Active) {
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const that = this;
        that.incidentService.activateIncident(incidentId, this.form.value).subscribe({
          next: () => {
            this.close();
          },
          async error(err) {
            if (err.error.code === 300006) {
              const incidentDetails = await firstValueFrom(that.incidentService.getIncidentDetails(incidentId));
              that.incidentEvent = incidentDetails.incidentEvents.filter(i => i.status === IncidentStatus.Active)[0];
              that.showForm = false;
            }
          },
        });
      } else if (this.status.value === IncidentStatus.Closed) {
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const that = this;
        that.incidentService.closeIncident(incidentId, this.form.value).subscribe({
          next: () => {
            this.close();
          },
          async error(err) {
            if (err.error.code === 300009) {
              const incidentDetails = await firstValueFrom(that.incidentService.getIncidentDetails(incidentId));
              that.incidentEvent = incidentDetails.incidentEvents.filter(i => i.status === IncidentStatus.Closed)[0];
              that.showForm = false;
            }
          },
        });
      }
    }
  }

  close() {
    this.dialogRef.close(true);
  }
}
